type LoaderProps = {
  src: string;
  width: number;
  quality?: number;
};

export default function imgixLoader({ src, width, quality }: LoaderProps): string {
  if (process.env.NODE_ENV !== 'production') {
    console.log('Image loader invoked:', { src, width, quality });
  }

  const baseUrl = process.env.NEXT_PUBLIC_IMGIX_URL || 'https://jola-888.imgix.net';
  const params = new URLSearchParams({
    auto: 'format,compress',
    fit: 'crop',
    crop: 'entropy,edges',
    w: width.toString(),
    q: (quality ?? 75).toString(),
  });

  // Hetzner-hosted media (park/community/fish images) live on our own box,
  // which jola-888 (an S3 source) cannot reach — so these historically
  // bypassed imgix entirely and shipped as ~700KB originals even when the
  // UI asked for a 120px thumbnail (the 2026-08-03 mobile-slowness root
  // cause). jola-media is a Web Folder imgix source over
  // app.trip-planner.com.au (created 2026-08-03): route them through it
  // with the same params S3 assets get.
  const mediaOrigin = 'https://app.trip-planner.com.au/media/';
  if (src.startsWith(mediaOrigin)) {
    const mediaBase = process.env.NEXT_PUBLIC_MEDIA_IMGIX_URL || 'https://jola-media.imgix.net';
    const mediaPath = src.slice('https://app.trip-planner.com.au'.length);
    return `${mediaBase}${mediaPath}?${params.toString()}`;
  }

  const isAbsoluteUrl = /^https?:\/\//i.test(src);

  if (isAbsoluteUrl) {
    // Our Imgix source (jola-888) is an S3 source, not a Web Proxy source.
    // It can only serve files stored in our S3 bucket — it cannot proxy
    // arbitrary external URLs.  Return external URLs directly so they load
    // from their origin server instead of getting a 404 from Imgix.
    return src;
  }

  // Local public-folder assets (e.g. /apps/campsite-explorer/images/default-campsite-hero.jpg)
  // are not in S3 so must be served directly by Next.js, not via Imgix.
  // IMPORTANT: Only match the public-folder path ({assetPrefix}/images/...).
  // Gallery images from S3 use /_assets/images/... paths and MUST go through Imgix.
  const assetPrefix = process.env.NEXT_PUBLIC_ASSET_PREFIX || '';
  const isLocalImage = assetPrefix
    ? src.startsWith(`${assetPrefix}/images/`)
    : src.startsWith('/images/');
  if (isLocalImage) {
    return src.startsWith('/') ? src : `/${src}`;
  }

  const normalised = src.startsWith('/') ? src : `/${src}`;
  const encodedPath = normalised
    .split('/')
    .map((segment, index) => (index === 0 ? segment : encodeURIComponent(segment)))
    .join('/');

  return `${baseUrl}${encodedPath}?${params.toString()}`;
}
